import type { ColumnDef } from '@tanstack/react-table';

import { type GetOrganizationsQuery, OrganizationState, OrganizationPaymentType } from 'data/__generated__';
import { textColumn, multiSelectColumn, booleanColumn, numberColumn } from 'components/DataTable';

import { StatusTag } from 'pages/Organizations/components/StatusTag';
import { PaymentType } from 'pages/Organizations/components/PaymentType';
import { ORGANIZATIONS_STATES_LABELS } from './states';
import { ORGANIZATION_PAYMENT_TYPE_LABELS } from './payment-types';

export type OrganizationRow = GetOrganizationsQuery['organizations'][number];

export type OrganizationFilterInput = {
    searchValue: string;
    state: OrganizationState[];
    paymentType: OrganizationPaymentType[];
};

export const organizationsColumns: ColumnDef<OrganizationRow, unknown>[] = [
    textColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: 'name',
        header: 'Nom',
        defaultVisible: true,
    }),
    textColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: '_id',
        header: 'Identifiant unique',
        defaultVisible: false,
    }),
    booleanColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: 'hasSubscription',
        header: 'Abonnement',
        defaultVisible: false,
    }),
    numberColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: 'nbActiveSite',
        header: 'Sites actifs',
        defaultVisible: true,
    }),
    numberColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: 'nbActiveTotem',
        header: 'Totems actifs',
        defaultVisible: true,
    }),
    multiSelectColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: 'paymentType',
        header: 'Type de paiement',
        filterKey: 'paymentType',
        defaultVisible: true,
        options: Object.values(OrganizationPaymentType).map((type) => ({
            value: type,
            label: ORGANIZATION_PAYMENT_TYPE_LABELS[type as keyof typeof ORGANIZATION_PAYMENT_TYPE_LABELS] ?? type,
        })),
        renderOption: (option) => <PaymentType paymentType={option.value as OrganizationPaymentType} />,
        cell: ({ row }) => <PaymentType paymentType={row.original.paymentType} />,
    }),
    multiSelectColumn<OrganizationRow, OrganizationFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(OrganizationState).map((state) => ({
            value: state,
            label: ORGANIZATIONS_STATES_LABELS[state as keyof typeof ORGANIZATIONS_STATES_LABELS] ?? state,
        })),
        renderOption: (option) => <StatusTag state={option.value as OrganizationState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
];
